import capitalize from 'lodash/capitalize';
import { useEffect, useState } from 'react';
import { useFormContext, useFormState, useController, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  selectIsJourneyGoalsFeatureFlagOn,
  selectIsJourneyGoalsWithExitsFeatureFlagOn,
  selectIsJourneySettingsRefactorFeatureFlagOn,
  selectUserRbacPermissions,
} from 'core/userContext/selectors';
import {
  SETTINGS_HAS_UNSAVED_CHANGES_MODAL,
  SETTINGS_SAVE_CHANGES_MODAL,
} from 'modules/workflows/constants';
import { openModal } from 'modules/workflows/ducks';
import { BUTTON_SIZES, BUTTON_VARIANTS, Button } from 'ui/button';
import { NavTabTrigger, NavTabsList, TabsContent, TabsGroup } from 'ui/tabsGroup';
import { colors, spacing } from 'ui/theme';
import { Takeover } from 'ui/workflows/takeover';
import { NegativeFillDiamond } from 'ui/icons/';
import noop from 'utils/noop';
import { useExitRuleContext } from 'modules/workflows/context/ExitRuleContext';
import { PROJECT_PERMISSIONS } from 'utils/permissions';
import isEmpty from 'lodash/isEmpty';
import { isEqual } from 'lodash';
import { ActionMenu } from 'ui/actionMenu';
import {
  ActionMenuOption,
  AllowedActionMenuElements,
  MENU_ALIGNMENTS,
  MENU_DIRECTIONS,
} from 'ui/actionMenu/types';
import { FullScreenTakeOver, PanelSize } from 'ui/takeOver';
import { AboutJourney } from './AboutJourney';
import { Conversions } from './Conversions/Conversions';
import { Metrics } from './Metrics';
import { SettingsTab } from './SettingsTakeover.constants';
import { SettingsTakeoverProps } from './SettingsTakeover.types';
import { getMetricsTab, getSettingsTabs } from './SettingsTakeover.utils';
import { SettingsTakeoverFormProvider } from './SettingsTakeoverFormProvider';
import { useSaveJourneySettings } from './hooks';
import { useSaveChangesModalBody } from './hooks/useSaveChangesModalBody';
import { JourneyExitRules } from './JourneyExitRules/JourneyExitRules';
import { Rules } from '../RuleBuilder/types';
import { RuleBuilderProvider, useRuleBuilder } from '../RuleBuilder/hooks/useRuleBuilder';
import { useInitialClonedRulesErrorCheck } from '../RuleBuilder/hooks/useInitialClonedRulesErrorCheck';
import { useTabValidation } from './hooks/useTabValidation';
import { StyledIconButton, ChevronUp, ComboButton } from './SettingsTakeover.styles';
import { useCloseSettings, useSaveSettings, useSwitchTab } from './SettingsTakeoverForm.hooks';

const StyledTakeoverBody = styled.div``;
const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${spacing.base};
  gap: ${spacing.base};
`;
const StyledTabsList = styled(NavTabsList)`
  padding-left: ${spacing.base};
  padding-right: ${spacing.base};
  width: 100%;
`;
const StyledAboutButtons = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;

const StyledNegativeFillDiamond = styled(NegativeFillDiamond)`
  margin-right: ${spacing.threeEights};
  path {
    fill: ${colors.red100} !important;
  }
`;

export const TakeoverCmpOld = ({
  onClose = noop,
  isOverlay = true,
  focusedTab = SettingsTab.About,
}: SettingsTakeoverProps) => {
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(focusedTab);

  const { invalidTabs, runValidation } = useTabValidation();

  const isJourneyGoalsEnabled = useSelector(selectIsJourneyGoalsFeatureFlagOn);
  const isJourneyGoalsWithExitsFeatureFlagOn = useSelector(
    selectIsJourneyGoalsWithExitsFeatureFlagOn,
  );
  const { getValues, trigger } = useFormContext();
  const { saveJourneySettings, isSaving } = useSaveJourneySettings({
    onSuccess: onClose,
    onSetTabValue: setTabValue,
  });

  const { dirtyFields, errors } = useFormState();
  const formValues = getValues();
  const modalBody = useSaveChangesModalBody(formValues, dirtyFields);

  const { isDirty } = useFormState();

  /* exit rules */
  const permissions = useSelector(selectUserRbacPermissions);

  const hasDraftMessaging = permissions.includes(PROJECT_PERMISSIONS.DraftMessaging);

  const { validationErrors, checkForValidationErrors } = useRuleBuilder();

  const handleOnSave = async () => {
    if (isJourneyGoalsEnabled) {
      // if we have the feature flag on and we have validation errors within the exit rules, we need to
      const validForm = await trigger();
      const exitRulesValidation = checkForValidationErrors();

      if (!validForm || exitRulesValidation) {
        runValidation();
        return;
      }

      if (isJourneyGoalsWithExitsFeatureFlagOn && exitRulesValidation) {
        setTabValue(SettingsTab.ExitRules);
        return;
      }
      dispatch(
        openModal(SETTINGS_SAVE_CHANGES_MODAL, {
          onSave: saveJourneySettings,
          onDiscard: onClose,
          modalBody,
        }),
      );
    } else {
      saveJourneySettings();
    }
  };
  const handleOnClose = () => {
    if (isDirty) {
      dispatch(
        openModal(SETTINGS_HAS_UNSAVED_CHANGES_MODAL, {
          onSave: saveJourneySettings,
          onDiscard: onClose,
          hasValidationErrors: !isEmpty(validationErrors) || !isEmpty(errors),
        }),
      );
    } else {
      onClose();
    }
  };

  const SETTINGS_TABS = getSettingsTabs(
    isJourneyGoalsEnabled,
    isJourneyGoalsWithExitsFeatureFlagOn,
  );
  const METRICS_TAB = getMetricsTab(isJourneyGoalsEnabled);

  const isDisabled =
    tabValue === SettingsTab.ExitRules
      ? !hasDraftMessaging || !isDirty || !isEmpty(validationErrors)
      : !hasDraftMessaging || !isDirty;
  return (
    <Takeover
      isLoading={false}
      isOverlay={isOverlay}
      onClose={handleOnClose}
      noPadding
      title="Settings"
      Body={
        <StyledTakeoverBody>
          {/* @ts-ignore */}
          <TabsGroup value={tabValue} onValueChange={setTabValue}>
            <StyledTabsList>
              {SETTINGS_TABS.map((tabName) => (
                <NavTabTrigger value={tabName} data-test={`${tabName}-tab`} key={tabName}>
                  {invalidTabs.length > 0 && invalidTabs.includes(tabName) && (
                    <StyledNegativeFillDiamond data-test={`${tabName}-invalid`} />
                  )}{' '}
                  {capitalize(tabName.replaceAll('-', ' '))}
                </NavTabTrigger>
              ))}
            </StyledTabsList>
            <TabsContent value={SettingsTab.About}>
              <StyledContent>
                <AboutJourney isDisabled={isSaving} />
              </StyledContent>
            </TabsContent>
            <TabsContent value={METRICS_TAB}>
              <StyledContent>
                {isJourneyGoalsEnabled && <Conversions isDisabled={isSaving} />}
                <Metrics isDisabled={isSaving} />
              </StyledContent>
            </TabsContent>
            {isJourneyGoalsWithExitsFeatureFlagOn && (
              <TabsContent value={SettingsTab.ExitRules}>
                <StyledContent>
                  <JourneyExitRules
                    goToConversionTab={() => setTabValue(SettingsTab.ConversionsAndMetrics)}
                  />
                </StyledContent>
              </TabsContent>
            )}
          </TabsGroup>
        </StyledTakeoverBody>
      }
      Footer={
        <StyledAboutButtons>
          <Button onClick={handleOnSave} isLoading={isSaving} isDisabled={isDisabled}>
            Save and exit
          </Button>
        </StyledAboutButtons>
      }
    />
  );
};

export const TakeoverCmpNew = ({ onClose = noop }: SettingsTakeoverProps) => {
  const { switchTab, isSaving: isSavingOnSwitch } = useSwitchTab();
  const { getValues } = useFormContext();
  const { isDirty } = useFormState();
  const tabValue = useWatch({ name: 'currentTab' });
  const formValues = getValues();

  const { invalidTabs } = useTabValidation(formValues?.currentTab);

  const isJourneyGoalsEnabled = useSelector(selectIsJourneyGoalsFeatureFlagOn);
  const isJourneyGoalsWithExitsFeatureFlagOn = useSelector(
    selectIsJourneyGoalsWithExitsFeatureFlagOn,
  );

  /* exit rules */
  const permissions = useSelector(selectUserRbacPermissions);
  const hasDraftMessaging = permissions.includes(PROJECT_PERMISSIONS.DraftMessaging);

  const { validationErrors } = useRuleBuilder();

  const { handleSave, handleSaveAndExit, isSaving } = useSaveSettings(onClose);
  const handleClose = useCloseSettings(onClose);

  const SETTINGS_TABS = getSettingsTabs(
    isJourneyGoalsEnabled,
    isJourneyGoalsWithExitsFeatureFlagOn,
  );
  const METRICS_TAB = getMetricsTab(isJourneyGoalsEnabled);

  const isDisabled =
    tabValue === SettingsTab.ExitRules
      ? !hasDraftMessaging || !isDirty || !isEmpty(validationErrors)
      : !hasDraftMessaging || !isDirty;

  const isTabDisabled = !hasDraftMessaging || isSaving || isSavingOnSwitch;

  const setViewOptions: ActionMenuOption[] = [
    {
      as: AllowedActionMenuElements.Button,
      content: 'Save and exit',
      id: 'btn-save-design-and-exit',
      onClick: handleSaveAndExit,
    },
  ];

  return (
    <FullScreenTakeOver
      onClose={handleClose}
      title="Settings"
      size={PanelSize.Large}
      Footer={
        <ComboButton>
          <Button
            onClick={handleSave}
            isLoading={isSaving || isSavingOnSwitch}
            isDisabled={isDisabled}
          >
            Save
          </Button>
          <ActionMenu
            isDisabled={isDisabled}
            align={MENU_ALIGNMENTS.RIGHT}
            direction={MENU_DIRECTIONS.TOP}
            options={setViewOptions}
          >
            <StyledIconButton
              isDisabled={isDisabled}
              data-test={'btn-save-design-action-menu'}
              size={BUTTON_SIZES.LARGE}
              variant={BUTTON_VARIANTS.PRIMARY}
            >
              <ChevronUp height={24} width={24} />
            </StyledIconButton>
          </ActionMenu>
        </ComboButton>
      }
    >
      <StyledTakeoverBody>
        {/* @ts-ignore */}
        <TabsGroup value={tabValue} onValueChange={switchTab}>
          <StyledTabsList>
            {SETTINGS_TABS.map((tabName) => (
              <NavTabTrigger value={tabName} data-test={`${tabName}-tab`} key={tabName}>
                {invalidTabs.length > 0 && invalidTabs.includes(tabName) && (
                  <StyledNegativeFillDiamond data-test={`${tabName}-invalid`} />
                )}{' '}
                {capitalize(tabName.replaceAll('-', ' '))}
              </NavTabTrigger>
            ))}
          </StyledTabsList>
          <TabsContent value={SettingsTab.About}>
            <StyledContent>
              <AboutJourney isDisabled={isTabDisabled} />
            </StyledContent>
          </TabsContent>
          <TabsContent value={METRICS_TAB}>
            <StyledContent>
              {isJourneyGoalsEnabled && <Conversions isDisabled={isTabDisabled} />}
              <Metrics isDisabled={isTabDisabled} />
            </StyledContent>
          </TabsContent>
          {isJourneyGoalsWithExitsFeatureFlagOn && (
            <TabsContent value={SettingsTab.ExitRules}>
              <StyledContent>
                <JourneyExitRules
                  goToConversionTab={() => switchTab(SettingsTab.ConversionsAndMetrics)}
                />
              </StyledContent>
            </TabsContent>
          )}
        </TabsGroup>
      </StyledTakeoverBody>
    </FullScreenTakeOver>
  );
};

const SettingsWithExitRules = (props: any) => {
  const isJourneySettingsFeatureFlagEnabled = useSelector(
    selectIsJourneySettingsRefactorFeatureFlagOn,
  );

  const TakeoverCmp = isJourneySettingsFeatureFlagEnabled ? TakeoverCmpNew : TakeoverCmpOld;
  const { exitRules, loadingExitRules, getExitRules } = useExitRuleContext();
  const {
    field: { onChange },
  } = useController({
    name: isJourneySettingsFeatureFlagEnabled ? 'exit-rules' : 'exitRules',
    defaultValue: exitRules,
  });

  useEffect(() => {
    if (isEmpty(exitRules)) {
      getExitRules();
    }
  }, []);

  const isClonedWithErrors = useInitialClonedRulesErrorCheck(exitRules);

  return (
    <RuleBuilderProvider
      storeRules={(rules: Rules) => {
        if (isEqual(exitRules, rules)) return;
        onChange(rules);
      }}
      rules={exitRules}
      isClonedWithErrors={isClonedWithErrors}
      isLoading={loadingExitRules}
    >
      <TakeoverCmp {...props} />
    </RuleBuilderProvider>
  );
};

export const SettingsTakeover = (props: SettingsTakeoverProps) => {
  return (
    <SettingsTakeoverFormProvider {...props}>
      <SettingsWithExitRules {...props} />
    </SettingsTakeoverFormProvider>
  );
};
